import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  Col,
  Flex,
  Form,
  Row,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import type { MenuProps } from 'antd';
import {
  CallFillIcon,
  DeleteIcon,
  CopyContent,
  InboundIcon,
  MailFillIcon,
  OutboundIcon,
  RightArrowIcon,
} from '@/assets/icons/icons';
import { PageHeaderActions } from '@/components';
import CommonDrawer from '@/components/CommonDrawer';
import CustomPagination from '@/components/CustomPagination';
import SettingsAgentDrawerForm, {
  AgentDrawerFormRef,
} from './SettingsAgentDrawerForm';
import type {
  AgentRow,
  AgentRowWithTooltip,
  Success7d,
  Usage7d,
} from './types';
import './AgentsTable.css';
import { CustomDeleteModal } from '@/pages/Modals';
import {
  CreateAgent,
  DeleteAgent,
  GetAgentsWithPaginationAndSearch,
} from '@/apis/settings';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux-toolkit/store';
import { getPermissions } from '@/common/helper/getPermissions';
import { useToken } from '@/common/helper';
import useUnauthorize from '@/hooks/useUnauthorize';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import { useSpin } from '@/components/SpinContext';
import { mapApiAgentToRow, relativeFromNow } from './agentFormMappers';
import AddButton from '@/components/AddButton';

/** Type + Collect + tbody action col (global.scss forces last td to 96px, not col 56). */
const AI_AGENTS_FIXED_COL_PX = 120 + 170 + 96;

const AI_AGENTS_FLEX_WEIGHTS = [0.9, 1.4, 0.9, 1, 0.95, 0.95, 1] as const;

const AI_AGENTS_FLEX_WEIGHT_SUM = AI_AGENTS_FLEX_WEIGHTS.reduce(
  (a, w) => a + w,
  0
);

type AiAgentsFlexWidths = {
  name: number;
  purpose: number;
  channel: number;
  last_triggered: number;
  usage_7d: number;
  success_7d: number;
  updated_label: number;
};

function computeAiAgentsFlexWidths(
  measuredWidth: number
): AiAgentsFlexWidths | null {
  const flexTotal = Math.max(0, measuredWidth - AI_AGENTS_FIXED_COL_PX);
  if (flexTotal <= 0) return null;

  const floors = AI_AGENTS_FLEX_WEIGHTS.map((w) =>
    Math.floor((flexTotal * w) / AI_AGENTS_FLEX_WEIGHT_SUM)
  );
  const remainder = flexTotal - floors.reduce((a, b) => a + b, 0);
  floors[1] += remainder;

  return {
    name: floors[0],
    purpose: floors[1],
    channel: floors[2],
    last_triggered: floors[3],
    usage_7d: floors[4],
    success_7d: floors[5],
    updated_label: floors[6],
  };
}
const LastTriggeredTooltipContent: React.FC<{
  meta?: AgentRowWithTooltip['last_triggered'];
}> = ({ meta }) => {
  const { formatDateTime } = useUserDateTimeFormat();
  const workflows = meta?.workflows || [];

  return (
    <div className='agents-lastTriggeredTooltip'>
      <div className='agents-lastTriggeredTooltip__title'>
        Last triggered (Workflows)
      </div>

      {workflows.length === 0 ? (
        <div className='agents-lastTriggeredTooltip__empty'>—</div>
      ) : (
        <div className='agents-lastTriggeredTooltip__list'>
          {workflows.map((wf) => (
            <div key={wf.id} className='agents-lastTriggeredTooltip__item'>
              <div
                className='agents-lastTriggeredTooltip__wfName'
                title={wf.name}
              >
                {wf.name}
              </div>

              <div className='agents-lastTriggeredTooltip__wfMeta'>
                <span>{formatDateTime(wf.triggered_at)}</span>
                <span className='agents-lastTriggeredTooltip__dot'>•</span>
                <span>{relativeFromNow(wf.triggered_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AgentsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const main_menu = useSelector((state: RootState) => state.menu.items);
  const AgentsPermissions = getPermissions(main_menu, 'Settings', 'AI agents');

  const [agents, setAgents] = useState<AgentRowWithTooltip[]>([]);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null);
  const drawerFormRef = useRef<AgentDrawerFormRef | null>(null);
  const [form] = Form.useForm();
  const { token } = useToken();
  const { formatDateTime } = useUserDateTimeFormat();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();

  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [refreshKey, setRefreshKey] = useState(0);

  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(1);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const pendingNavRef = useRef<null | (() => void)>(null);
  const [expandedLastTriggeredIds, setExpandedLastTriggeredIds] = useState<
    Set<string>
  >(() => new Set());

  const [createChannelType, setCreateChannelType] = useState<
    'outbound' | 'inbound' | null
  >(null);

  const toggleLastTriggered = useCallback((id: string) => {
    setExpandedLastTriggeredIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openDrawerForAgent = useCallback((agentIdOrNull: string | null) => {
    setActiveAgentId(agentIdOrNull);
    setShowDrawer(true);
  }, []);

  const openDeleteModal = useCallback((id: string) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  }, []);
  const useIsBelow1024 = () => {
    const [isBelow, setIsBelow] = useState(() =>
      typeof window !== 'undefined'
        ? window.matchMedia('(max-width: 1024px)').matches
        : false
    );

    useEffect(() => {
      const mq = window.matchMedia('(max-width: 1024px)');
      const onChange = (e: MediaQueryListEvent) => setIsBelow(e.matches);

      setIsBelow(mq.matches);

      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else mq.addListener(onChange);

      return () => {
        if (mq.removeEventListener) mq.removeEventListener('change', onChange);
        else mq.removeListener(onChange);
      };
    }, []);

    return isBelow;
  };
  const isBelow1024 = useIsBelow1024();

  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [tableWrapWidth, setTableWrapWidth] = useState(0);

  useEffect(() => {
    const el = tableWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setTableWrapWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const aiAgentsFlexWidths = useMemo(() => {
    if (isBelow1024 || tableWrapWidth <= 0) return null;
    return computeAiAgentsFlexWidths(tableWrapWidth);
  }, [isBelow1024, tableWrapWidth]);

  const confirmDelete = useCallback(() => {
    if (!selectedDeleteId) return;
    if (!token) {
      message.error('Token missing');
      return;
    }

    showSpin();

    DeleteAgent(selectedDeleteId, token)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          message.success(data?.message || 'Deleted successfully');
          setAgents((prev) => prev.filter((a) => a.id !== selectedDeleteId));
          setTotalCount((prev) => Math.max(0, prev - 1));
          closeDeleteModal();
          setShowDrawer(false);
          setActiveAgentId(null);
        } else {
          message.error(data?.message || 'Error deleting agent');
        }
      })
      .catch((error) => {
        unauthorize(error);
        message.error('Error deleting agent');
      })
      .finally(() => {
        hideSpin();
      });
  }, [
    selectedDeleteId,
    token,
    showSpin,
    hideSpin,
    closeDeleteModal,
    unauthorize,
  ]);

  const closeDrawerNow = useCallback(() => {
    setShowDrawer(false);
    setActiveAgentId(null);
    setCreateChannelType(null);
    form.resetFields();
  }, [form]);

  const requestCloseDrawer = useCallback(() => {
    const hasUnsaved = drawerFormRef.current?.hasUnsavedChanges?.() ?? false;

    if (hasUnsaved) {
      pendingNavRef.current = closeDrawerNow;
      setShowDiscardModal(true);
      return;
    }

    closeDrawerNow();
  }, [closeDrawerNow]);

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardModal(false);
    const fn = pendingNavRef.current;
    pendingNavRef.current = null;
    fn?.();
  }, []);

  const activeAgent = useMemo<AgentRowWithTooltip | null>(() => {
    return agents.find((a) => a.id === activeAgentId) || null;
  }, [agents, activeAgentId]);

  const totalPages = Math.max(1, pageCount || 1);
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value || '');
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage > 0 ? newPage : 1);
  }, []);

  const handleCopy = useCallback(async () => {
    if (!token) return message.error('Token missing');

    const formApi = drawerFormRef.current;
    if (!formApi) return message.error('Form not ready');

    try {
      showSpin();
      const payload = await formApi.getCreatePayload();
      const { id: _id, ...createPayload } = payload;

      const res = await CreateAgent(token, createPayload);
      const data = res?.data?.data;

      if (!data?.is_success) {
        return message.error(data?.message || 'Error copying agent');
      }

      message.success(data?.message || 'Agent copied');
      setRefreshKey((k) => k + 1);
      setShowDrawer(false);
      setActiveAgentId(null);
    } catch {
      message.error('Fix validation errors before copying');
    } finally {
      hideSpin();
    }
  }, [token, showSpin, hideSpin]);

  useEffect(() => {
    if (!token) return;

    showSpin();

    GetAgentsWithPaginationAndSearch(token, search, page)
      .then((res) => {
        const payload = res?.data?.data;
        const list = payload?.data ?? [];
        setAgents(list.map((a) => mapApiAgentToRow(a, formatDateTime)));
        setTotalCount(payload?.total_count ?? list.length);
        setPageCount(payload?.page_count ?? 1);
      })
      .catch((error) => {
        unauthorize(error);
        setAgents([]);
      })
      .finally(hideSpin);
  }, [
    token,
    page,
    search,
    refreshKey,
    showSpin,
    hideSpin,
    unauthorize,
    formatDateTime,
  ]);

  useEffect(() => {
    const agentId = searchParams.get('agent_id');
    if (!agentId || agents.length === 0) return;
    openDrawerForAgent(agentId);
    setSearchParams({}, { replace: true });
  }, [agents, searchParams, openDrawerForAgent, setSearchParams]);

  const TableHeaderText: React.FC<{ text: string }> = ({ text }) => (
    <Typography.Text ellipsis={{ tooltip: text }} style={{ width: '100%' }}>
      {text}
    </Typography.Text>
  );

  const createMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'outbound',
        label: (
          <Flex align='center' gap={10}>
            <OutboundIcon height={12} width={12} />
            <span className='agents-createMenuLabel'>Outbound</span>
          </Flex>
        ),
        onClick: () => {
          setCreateChannelType('outbound');
          openDrawerForAgent(null);
        },
      },
      {
        key: 'inbound',
        label: (
          <Flex align='center' gap={10}>
            <InboundIcon size={12} />
            <span className='agents-createMenuLabel'>Inbound</span>
          </Flex>
        ),
        onClick: () => {
          setCreateChannelType('inbound');
          openDrawerForAgent(null);
        },
      },
    ],
    [openDrawerForAgent]
  );

  const columns = useMemo(
    () => [
      {
        title: <TableHeaderText text='Agent name' />,
        dataIndex: 'name',
        key: 'name',
        ...(aiAgentsFlexWidths ? { width: aiAgentsFlexWidths.name } : {}),
        render: (text: string) => (
          <Typography.Text
            ellipsis={{ tooltip: text }}
            style={{ width: '100%' }}
          >
            {text}
          </Typography.Text>
        ),
      },
      {
        title: <TableHeaderText text='Purpose' />,
        dataIndex: 'purpose',
        key: 'purpose',
        ...(aiAgentsFlexWidths ? { width: aiAgentsFlexWidths.purpose } : {}),
        render: (_: string, record: AgentRowWithTooltip) => {
          const isDefault =
            record?.purpose_name?.toLowerCase() === 'default' ||
            record?.purpose_name?.toLowerCase() === 'default platform agent';

          if (isDefault) {
            return <span className='agents-defaultBadge'>Default</span>;
          }

          const purposeText = record?.purpose || '--';

          return (
            <Typography.Text
              ellipsis={{ tooltip: purposeText }}
              style={{ width: '100%' }}
            >
              {purposeText}
            </Typography.Text>
          );
        },
      },
      {
        title: <TableHeaderText text='Channel(s)' />,
        dataIndex: 'channel',
        key: 'channel',
        ...(aiAgentsFlexWidths ? { width: aiAgentsFlexWidths.channel } : {}),
        onCell: () => ({
          style: { minWidth: 72 },
        }),
        render: (channel: AgentRow['channel']) => {
          const items = Array.isArray(channel) ? channel : [];

          if (!items.length) return <div>--</div>;

          const tooltipContent = (
            <div className='agents-channelTooltip'>
              {items.map((ch) => (
                <div
                  key={`${ch.type}-${ch.value}`}
                  className='agents-channelTooltip-row'
                >
                  {ch.type === 'phone' ? (
                    <CallFillIcon color='#FFFFFF' />
                  ) : (
                    <MailFillIcon color='#FFFFFF' />
                  )}
                  <span>{ch.value}</span>
                </div>
              ))}
            </div>
          );

          return (
            <Tooltip
              title={tooltipContent}
              overlayClassName='agents-channelOverlay'
              placement='top'
              arrow={false}
            >
              <Flex gap={10}>
                {items.map((ch) =>
                  ch.type === 'phone' ? (
                    <CallFillIcon key={`${ch.type}-${ch.value}`} />
                  ) : (
                    <MailFillIcon key={`${ch.type}-${ch.value}`} />
                  )
                )}
              </Flex>
            </Tooltip>
          );
        },
      },
      {
        title: <TableHeaderText text='Type' />,
        dataIndex: 'agent_type',
        key: 'agent_type',
        width: 120,
        render: (val: AgentRow['agent_type']) => {
          const raw = (val || '').toLowerCase();
          const isInbound = raw === 'inbound';
          const isOutbound = raw === 'outbound';
          const label = isInbound ? 'Inbound' : isOutbound ? 'Outbound' : '--';

          return (
            <Flex align='center' gap={4} className='agents-typeCell'>
              {(isInbound || isOutbound) && (
                <span className='agents-typeCellIcon'>
                  {isInbound ? <InboundIcon /> : <OutboundIcon />}
                </span>
              )}

              <Typography.Text
                ellipsis={{ tooltip: label }}
                style={{ width: '100%', display: 'inline-block', minWidth: 0 }}
              >
                {label}
              </Typography.Text>
            </Flex>
          );
        },
      },
      {
        title: <TableHeaderText text='Collect' />,
        dataIndex: 'collect',
        key: 'collect',
        width: 170,
        render: (collect: AgentRow['collect']) => {
          const items = Array.isArray(collect) ? collect : [];
          if (items.length === 0) return <div>--</div>;

          const labels = items.map((c) => c.label);
          const first = labels[0] || '--';
          const restCount = labels.length - 1;

          return (
            <Flex align='center' gap={8}>
              <Tooltip title={first} placement='top'>
                <span className='agents-collectPill'>
                  {first.length > 17 ? `${first.slice(0, 17)}...` : first}
                </span>
              </Tooltip>

              {restCount > 0 && (
                <Tooltip title={labels.slice(1).join(', ')} placement='top'>
                  <span className='agents-collectPill agents-collectPill--clickable'>
                    +{restCount}
                  </span>
                </Tooltip>
              )}
            </Flex>
          );
        },
      },
      {
        title: <TableHeaderText text='Last triggered' />,
        dataIndex: 'last_triggered_label',
        key: 'last_triggered',
        ...(aiAgentsFlexWidths
          ? { width: aiAgentsFlexWidths.last_triggered }
          : {}),
        render: (_label: string, record: AgentRowWithTooltip) => {
          const relative =
            record?.last_triggered?.relative_label ||
            record?.last_triggered_label ||
            '--';

          const iso =
            record?.last_triggered?.at || record?.last_triggered_at || null;

          const isExpanded = expandedLastTriggeredIds.has(record.id);

          const displayText =
            isExpanded && iso ? formatDateTime(iso) : relative;

          const hasWorkflows =
            (record?.last_triggered?.workflows || []).length > 0;

          return (
            <span
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                toggleLastTriggered(record.id);
              }}
              className={
                iso ? 'agents-lastTriggeredCell--clickable' : undefined
              }
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleLastTriggered(record.id);
                }
              }}
            >
              <Tooltip
                placement='top'
                arrow={false}
                overlayInnerStyle={{
                  padding: 12,
                  borderRadius: 12,
                  backgroundColor: 'var(--surface-container)',
                  color: 'var(--text-primary)',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)',
                }}
                title={
                  <LastTriggeredTooltipContent meta={record?.last_triggered} />
                }
                trigger={hasWorkflows ? ['hover'] : []}
                destroyTooltipOnHide
              >
                <Typography.Text
                  ellipsis={{ tooltip: displayText }}
                  style={{ width: '100%', display: 'inline-block' }}
                >
                  {displayText}
                </Typography.Text>
              </Tooltip>
            </span>
          );
        },
      },
      {
        title: (
          <Tooltip title='Number of interactions handled in the past 7 days'>
            <TableHeaderText text='Usage (7d)' />
          </Tooltip>
        ),
        dataIndex: 'usage_7d',
        key: 'usage_7d',
        ...(aiAgentsFlexWidths ? { width: aiAgentsFlexWidths.usage_7d } : {}),
        render: (_: Usage7d, record: AgentRowWithTooltip) => {
          const usage = record.usage_7d;
          if (!usage) return <span className='agents-text12'>--</span>;

          const call = Number(usage.call ?? 0);
          const email = Number(usage.email ?? 0);
          const total = Number(usage.total ?? call + email);

          const tooltipContent = (
            <div className='agents-metricsTooltip-box'>
              <div className='agents-metricsTooltip-title'>Usage (7d)</div>
              <div className='agents-metricsTooltip-divider' />

              <div className='agents-metricsTooltip-row'>
                <div className='agents-metricsTooltip-label'>
                  <CallFillIcon color='var(--text-secondary)' /> Call
                </div>
                <span>{call}</span>
              </div>

              <div className='agents-metricsTooltip-row'>
                <div className='agents-metricsTooltip-label'>
                  <MailFillIcon color='var(--text-secondary)' /> Email
                </div>
                <span>{email}</span>
              </div>

              <div className='agents-metricsTooltip-divider' />

              <div className='agents-metricsTooltip-total'>
                <span>Total</span>
                <span>{total}</span>
              </div>
            </div>
          );

          return (
            <Tooltip
              autoAdjustOverflow
              title={tooltipContent}
              color='transparent'
              overlayInnerStyle={{ padding: 0 }}
              overlayClassName='agents-metricsOverlay'
              placement='top'
            >
              <span className='agents-text12'>{total}</span>
            </Tooltip>
          );
        },
      },
      {
        title: (
          <Tooltip title='Success rate in the past 7 days'>
            <TableHeaderText text='Success (7d)' />
          </Tooltip>
        ),
        dataIndex: 'success_7d',
        key: 'success_7d',
        ...(aiAgentsFlexWidths ? { width: aiAgentsFlexWidths.success_7d } : {}),
        render: (_: Success7d, record: AgentRowWithTooltip) => {
          const success = record.success_7d;
          if (!success) return <span className='agents-text12'>--</span>;

          const call = success.call ?? '0%';
          const email = success.email ?? '0%';
          const total = success.total ?? '0%';

          const tooltipContent = (
            <div className='agents-metricsTooltip-box'>
              <div className='agents-metricsTooltip-title'>Success (7d)</div>
              <div className='agents-metricsTooltip-divider' />

              <div className='agents-metricsTooltip-row'>
                <div className='agents-metricsTooltip-label'>
                  <CallFillIcon color='var(--text-secondary)' /> Call
                </div>
                <span>{call}</span>
              </div>

              <div className='agents-metricsTooltip-row'>
                <div className='agents-metricsTooltip-label'>
                  <MailFillIcon color='var(--text-secondary)' /> Email
                </div>
                <span>{email}</span>
              </div>

              <div className='agents-metricsTooltip-divider' />

              <div className='agents-metricsTooltip-total'>
                <span>Total</span>
                <span>{total}</span>
              </div>
            </div>
          );

          return (
            <Tooltip
              autoAdjustOverflow
              title={tooltipContent}
              color='transparent'
              overlayInnerStyle={{ padding: 0 }}
              overlayClassName='agents-metricsOverlay'
              placement='top'
            >
              <span className='agents-text12'>{total}</span>
            </Tooltip>
          );
        },
      },
      {
        title: <TableHeaderText text='Last updated' />,
        dataIndex: 'updated_label',
        key: 'updated_label',
        ...(aiAgentsFlexWidths
          ? { width: aiAgentsFlexWidths.updated_label }
          : {}),
        render: (text: string) => {
          const value = text || '--';
          return (
            <Typography.Text
              ellipsis={{ tooltip: value }}
              style={{ width: '100%' }}
            >
              {value}
            </Typography.Text>
          );
        },
      },
      {
        title: '',
        key: 'action',
        width: 56,
        render: (_: unknown, record: AgentRowWithTooltip) => (
          <Flex justify='flex-end' align='center'>
            <div
              onClick={(e) => {
                e.stopPropagation();
                openDrawerForAgent(record?.id);
              }}
              aria-label='Open agent'
              className='agents-openAgentBtn'
            >
              <RightArrowIcon width={16} height={16} />
            </div>
          </Flex>
        ),
      },
    ],
    [
      aiAgentsFlexWidths,
      expandedLastTriggeredIds,
      openDrawerForAgent,
      toggleLastTriggered,
      formatDateTime,
    ]
  );

  return (
    <>
      <div className='settings-content-wrapper'>
        <PageHeaderActions
          showToggle={false}
          title='AI agents'
          totalCount={totalCount}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          addButtonWidth={115}
          addButtonText='Create agent'
          onSearchChange={handleSearchChange}
          onSearchClear={() => handleSearchChange('')}
          showAddButton={Boolean(AgentsPermissions?.is_create)}
          onAddClick={() => {}}
          addButtonWrapper={
            <Dropdown trigger={['click']} menu={{ items: createMenuItems }}>
              <div>
                <AddButton
                  addButtonWidth={115}
                  fontSize='12px'
                  fontWeight='600'
                  text='Create agent'
                  onClick={() => {}}
                />
              </div>
            </Dropdown>
          }
        />

        <div ref={tableWrapRef} className='agents-tableWrap'>
          <Table
            tableLayout='fixed'
            columns={columns}
            dataSource={agents}
            pagination={false}
            scroll={isBelow1024 ? { x: 'max-content' } : undefined}
            className='settings-table ai-agents-settings-table'
            rowHoverable={false}
            rowKey={(r) => r.id}
            rowClassName={(record: AgentRowWithTooltip) =>
              record?.purpose_name?.toLowerCase() === 'default' ||
              record?.purpose_name?.toLowerCase() === 'default platform agent'
                ? 'agents-defaultRow'
                : ''
            }
            onRow={(record) => ({
              onClick: () => {
                const hasUnsaved =
                  drawerFormRef.current?.hasUnsavedChanges?.() ?? false;

                if (showDrawer && hasUnsaved) {
                  pendingNavRef.current = () => openDrawerForAgent(record?.id);
                  setShowDiscardModal(true);
                  return;
                }

                openDrawerForAgent(record?.id);
              },
            })}
          />
        </div>

        <Row className='agents-paginationRow' justify='center'>
          <Col xs={24} className='flex-to-block'>
            {totalCount > 0 && (
              <Flex className='pagination-alignment'>
                <CustomPagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </Flex>
            )}
          </Col>
        </Row>
      </div>

      {showDrawer && (
        <CommonDrawer
          open={showDrawer}
          onClose={requestCloseDrawer}
          headerActions={
            activeAgentId ? (
              <Flex align='center' gap={8}>
                <span
                  onClick={handleCopy}
                  className='agents-headerIconBtn'
                  aria-label='Copy'
                >
                  <CopyContent size={30} />
                </span>
                {AgentsPermissions?.is_delete &&
                  !(
                    activeAgent?.purpose?.toLowerCase() === 'default' ||
                    activeAgent?.purpose?.toLowerCase() ===
                      'default platform agent'
                  ) && (
                    <span
                      onClick={() => {
                        if (activeAgent?.id) openDeleteModal(activeAgent.id);
                      }}
                      className='agents-headerIconBtn'
                      aria-label='Delete'
                    >
                      <DeleteIcon />
                    </span>
                  )}
              </Flex>
            ) : null
          }
        >
          <SettingsAgentDrawerForm
            ref={drawerFormRef}
            agentData={activeAgentId ? (activeAgent as AgentRow) : null}
            drawerOpen={showDrawer}
            onClose={requestCloseDrawer}
            onSaved={() => setRefreshKey((k) => k + 1)}
            initialChannelType={createChannelType}
            readOnly={
              activeAgentId
                ? !AgentsPermissions?.is_update
                : !AgentsPermissions?.is_create
            }
          />
        </CommonDrawer>
      )}

      <CustomDeleteModal
        visible={showDiscardModal}
        onConfirm={() => handleConfirmDiscard()}
        onClose={() => {
          pendingNavRef.current = null;
          setShowDiscardModal(false);
        }}
        title='Discard changes?'
        message=''
        cancelText='Keep editing'
        confirmText='Discard'
        confirmColor='var(--text-primary)'
      />

      <CustomDeleteModal
        visible={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title='Delete agent?'
        message='Are you sure you want to delete this agent? This action can’t be undone.'
        cancelText='Cancel'
        confirmText='Delete'
        confirmColor='var(--danger)'
      />
    </>
  );
};

export default AgentsTable;
